import React from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface AIReportFeedback {
  id: number;
  userEmail: string;
  userId: number;
  feedbackType: 'super_like' | 'like' | 'dislike';
  periodStart: string;
  periodEnd: string;
  periodType: string;
  aiReportId: number;
  createdAt: string;
}

export interface AdminAIReportFeedbacksPageProps {
  feedbacks: AIReportFeedback[];
  statistics: {
    total: number;
    superLikes: number;
    likes: number;
    dislikes: number;
  };

  // URLs
  dashboardPath: string;
  feedbackPath: (aiReportId: number) => string;
  userPath: (id: number) => string;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    backLabel: string;
    // Stats
    superLikesLabel: string;
    likesLabel: string;
    dislikesLabel: string;
    ofTotal: string;
    // Summary
    feedbackSummaryTitle: string;
    totalFeedbackSubmissions: string;
    noFeedbackYet: string;
    // Super/Like labels for progress bar
    superLabel: string;
    likesBarLabel: string;
    dislikesBarLabel: string;
    // Table
    recentFeedbackTitle: string;
    userColumn: string;
    reportPeriodColumn: string;
    feedbackTypeColumn: string;
    submittedColumn: string;
    actionsColumn: string;
    viewReportButton: string;
    agoLabel: string;
    noFeedbackSubmittedYet: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPeriodDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatPeriodDateWithYear(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function FeedbackTypeBadge({ type }: { type: AIReportFeedback['feedbackType'] }) {
  if (type === 'super_like') {
    return (
      <span className="badge bg-success">
        <i className="bi bi-hand-thumbs-up-fill" />
        <i className="bi bi-hand-thumbs-up-fill" /> Super Like
      </span>
    );
  }
  if (type === 'like') {
    return (
      <span className="badge bg-primary">
        <i className="bi bi-hand-thumbs-up" /> Like
      </span>
    );
  }
  return (
    <span className="badge bg-danger">
      <i className="bi bi-hand-thumbs-down" /> Dislike
    </span>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminAIReportFeedbacksPage({
  feedbacks,
  statistics,
  dashboardPath,
  feedbackPath,
  userPath,
  labels,
}: AdminAIReportFeedbacksPageProps) {
  const superLikesPercent = statistics.total > 0
    ? ((statistics.superLikes / statistics.total) * 100).toFixed(1)
    : '0';
  const likesPercent = statistics.total > 0
    ? ((statistics.likes / statistics.total) * 100).toFixed(1)
    : '0';
  const dislikesPercent = statistics.total > 0
    ? ((statistics.dislikes / statistics.total) * 100).toFixed(1)
    : '0';

  return (
    <div>
      <AdminPageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        icon="chat-square-heart"
        backLink={{ href: dashboardPath, label: labels.backLabel }}
      />

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-6 col-xl-4">
          <AdminStatCard
            title={labels.superLikesLabel}
            value={statistics.superLikes}
            subtitle={statistics.total > 0 ? `${superLikesPercent}% ${labels.ofTotal}` : ''}
            icon="hand-thumbs-up-fill"
            iconColor="success"
            borderColor="success"
            valueColor="success"
          />
        </div>
        <div className="col-md-6 col-xl-4">
          <AdminStatCard
            title={labels.likesLabel}
            value={statistics.likes}
            subtitle={statistics.total > 0 ? `${likesPercent}% ${labels.ofTotal}` : ''}
            icon="hand-thumbs-up"
            iconColor="primary"
            borderColor="primary"
            valueColor="primary"
          />
        </div>
        <div className="col-md-6 col-xl-4">
          <AdminStatCard
            title={labels.dislikesLabel}
            value={statistics.dislikes}
            subtitle={statistics.total > 0 ? `${dislikesPercent}% ${labels.ofTotal}` : ''}
            icon="hand-thumbs-down"
            iconColor="danger"
            borderColor="danger"
            valueColor="danger"
          />
        </div>
      </div>

      {/* Feedback Summary */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-bar-chart me-2" />
            {labels.feedbackSummaryTitle}
          </h5>
          <div className="row align-items-center">
            <div className="col-md-3">
              <h3 className="mb-0">{statistics.total.toLocaleString()}</h3>
              <p className="text-muted mb-0">{labels.totalFeedbackSubmissions}</p>
            </div>
            <div className="col-md-9">
              <div className="progress" style={{ height: 30 }}>
                {statistics.total > 0 ? (
                  <>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${(statistics.superLikes / statistics.total) * 100}%` }}
                    >
                      {statistics.superLikes > 0 && `${statistics.superLikes} ${labels.superLabel}`}
                    </div>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${(statistics.likes / statistics.total) * 100}%` }}
                    >
                      {statistics.likes > 0 && `${statistics.likes} ${labels.likesBarLabel}`}
                    </div>
                    <div
                      className="progress-bar bg-danger"
                      role="progressbar"
                      style={{ width: `${(statistics.dislikes / statistics.total) * 100}%` }}
                    >
                      {statistics.dislikes > 0 && `${statistics.dislikes} ${labels.dislikesBarLabel}`}
                    </div>
                  </>
                ) : (
                  <div className="progress-bar bg-secondary" role="progressbar" style={{ width: '100%' }}>
                    {labels.noFeedbackYet}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-list-ul me-2" />
            {labels.recentFeedbackTitle}
          </h5>
        </div>
        <div className="card-body p-0">
          {feedbacks.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{labels.userColumn}</th>
                    <th>{labels.reportPeriodColumn}</th>
                    <th>{labels.feedbackTypeColumn}</th>
                    <th>{labels.submittedColumn}</th>
                    <th>{labels.actionsColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.map((feedback) => (
                    <tr key={feedback.id}>
                      <td>
                        <a href={userPath(feedback.userId)} className="text-decoration-none">
                          <strong>{feedback.userEmail}</strong>
                        </a>
                      </td>
                      <td>
                        {formatPeriodDate(feedback.periodStart)} -{' '}
                        {formatPeriodDateWithYear(feedback.periodEnd)}
                        <br />
                        <small className="text-muted">
                          {feedback.periodType.charAt(0).toUpperCase() + feedback.periodType.slice(1)}
                        </small>
                      </td>
                      <td>
                        <FeedbackTypeBadge type={feedback.feedbackType} />
                      </td>
                      <td>
                        {formatTimeAgo(feedback.createdAt)} {labels.agoLabel}
                        <br />
                        <small className="text-muted">{formatDate(feedback.createdAt)}</small>
                      </td>
                      <td>
                        <a
                          href={feedbackPath(feedback.aiReportId)}
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="bi bi-eye" /> {labels.viewReportButton}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-chat-square-heart" style={{ fontSize: '3rem' }} />
              <p className="mt-3 mb-0">{labels.noFeedbackSubmittedYet}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAIReportFeedbacksPage;
